import { useEffect, useMemo, useState } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { GET_MAPS_CONFIGURATION } from '../../graphql/maps/get-maps-configuration';
import { yandexMapStyleUpdatedAtVar } from '../../graphql/configuration/configuration.cache';
import { YANDEX_MAP_CUSTOM_STYLE } from '../../constants/map-styles';

const STORAGE_KEY_STYLES = 'yandex-map-styles';
const STORAGE_KEY_TIMESTAMP = 'yandex-map-styles-timestamp';

// Stable reference so we never trigger re-renders by creating a new array
const DEFAULT_STYLES = [...YANDEX_MAP_CUSTOM_STYLE];

export const useMapStyles = () => {
  const [mapStyles, setMapStyles] = useState<any[] | null>(null);
  const configTimestamp = useReactiveVar(yandexMapStyleUpdatedAtVar);
  const cachedTimestamp = localStorage.getItem(STORAGE_KEY_TIMESTAMP);
  const shouldFetch = configTimestamp && cachedTimestamp !== configTimestamp;
  const cachedStyles = localStorage.getItem(STORAGE_KEY_STYLES);

  const { data, loading, error } = useQuery(GET_MAPS_CONFIGURATION, {
    variables: { platform: 'WEB' },
    context: { clientName: 'anonymous' },
    skip: !shouldFetch,
  });

  useEffect(() => {
    let isMounted = true;

    if (cachedStyles && !shouldFetch) {
      try {
        const parsed = JSON.parse(cachedStyles);
        if (isMounted) {
          setMapStyles(parsed);
        }
      } catch (err) {
        if (isMounted) {
          setMapStyles(DEFAULT_STYLES);
        }
      }
    }

    return () => {
      isMounted = false;
    };
  }, [shouldFetch, cachedStyles]);

  useEffect(() => {
    let isMounted = true;

    if (data?.mapsConfiguration && !cachedStyles && shouldFetch) {
      try {
        const styles = JSON.parse(data.mapsConfiguration);
        if (isMounted) {
          setMapStyles(styles);
        }
        localStorage.setItem(STORAGE_KEY_STYLES, data.mapsConfiguration);
        if (configTimestamp) {
          localStorage.setItem(STORAGE_KEY_TIMESTAMP, configTimestamp);
        }
      } catch (err) {
        if (isMounted) {
          setMapStyles(DEFAULT_STYLES);
        }
      }
    } else if (error) {
      // Use callback form to avoid depending on mapStyles in the dep array
      setMapStyles((prev) => (prev === null ? DEFAULT_STYLES : prev));
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, configTimestamp, shouldFetch, cachedStyles]);

  const result = useMemo(() => mapStyles || DEFAULT_STYLES, [mapStyles]);

  return {
    mapStyles: result,
    loading,
  };
};
